import type { CentrixPathsByMethod } from "core/entities/APITypes";
import { useCentrixApiPreload } from "core/hooks/useCentrixApi";
import { useDebouncedCallback } from "core/hooks/useDebouncedCallback";
import { useLocale } from "core/hooks/useLocale";
import { exhaustiveCheck } from "core/utils/exhaustiveCheck";
import type { DmsDocNodeType } from "features/DMS/entities";

export function usePreloadDmsNode() {
  const locale = useLocale();
  const centrixPreload = useCentrixApiPreload();
  return useDebouncedCallback(
    (
      path: string,
      type: DmsDocNodeType = "Document",
      mode: "manage" | "view"
    ) => {
      const summaryPath =
        mode === "manage"
          ? ("/api/app/doc-node-manage/summary-with-parents" as const satisfies CentrixPathsByMethod<"get">)
          : ("/api/app/doc-node/summary-with-parents" as const satisfies CentrixPathsByMethod<"get">);
      const childrenPath =
        mode === "manage"
          ? ("/api/app/doc-node-manage/direct-child-summary-list" as const satisfies CentrixPathsByMethod<"get">)
          : ("/api/app/doc-node/direct-child-summary-list" as const satisfies CentrixPathsByMethod<"get">);

      switch (type) {
        case "Library": {
          centrixPreload({
            path: "/api/app/doc-node/library-summary", // libraries have a different endpoint
            parameters: { query: { locale, path, isEdit: mode === "manage" } },
          });
          centrixPreload({
            path: childrenPath,
            parameters: { query: { path, locale } },
          });
          break;
        }
        case "Folder": {
          centrixPreload({
            path: summaryPath,
            parameters: { query: { locale, path } },
          });
          centrixPreload({
            path: childrenPath,
            parameters: { query: { path, locale } },
          });
          break;
        }
        case "Document": {
          centrixPreload({
            path: summaryPath,
            parameters: { query: { locale, path } },
          });
          break;
        }
        case "Invalid":
          break;
        default: {
          exhaustiveCheck(type);
        }
      }
    },
    [centrixPreload, locale],
    300
  );
}
